import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useEscape } from './hooks';

export function Panel({ title, meta, children, className = '' }: { title?: string; meta?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`panel ${className}`}>
      {(title || meta) && (
        <header className="panel-head">
          {title && <h2 className="panel-title">{title}</h2>}
          {meta && <span className="panel-meta">{meta}</span>}
        </header>
      )}
      {children}
    </section>
  );
}

export type PeriodState = 'Open Plan' | 'Published Actuals' | 'Review' | 'As of';

export function StateBadge({ state, children }: { state: PeriodState; children?: ReactNode }) {
  const cls = state === 'Published Actuals' ? 'published' : state === 'Review' ? 'review' : state === 'Open Plan' ? 'open' : 'asof';
  const word = state === 'Published Actuals' ? 'Sealed' : state === 'Review' ? 'In review' : state === 'Open Plan' ? 'Open plan' : 'As recorded';
  return <span className={`badge badge-${cls}`}>{children ?? word}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <p className="empty" aria-busy="true">{label}</p>;
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-note" role="alert">
      <strong>This could not load.</strong> {message}
      {onRetry && <button className="link" onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function Stat({ label, value, foot, onClick }: { label: string; value: ReactNode; foot?: ReactNode; onClick?: () => void }) {
  const body = (
    <>
      <span className="stat-label">{label}</span>
      <strong className="stat-value">{value}</strong>
      {foot && <span className="stat-foot">{foot}</span>}
    </>
  );
  return onClick
    ? <button type="button" className="stat stat-link" onClick={onClick}>{body}</button>
    : <div className="stat">{body}</div>;
}

export function Bar({ value, max, over }: { value: number; max: number; over?: boolean }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return <span className={`bar ${over ? 'bar-over' : ''}`} aria-hidden="true"><i style={{ width: `${pct}%` }} /></span>;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const EXIT_MS = 200;
const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
/** Scroll-style momentum projection: where a flick would come to rest (Designing Fluid Interfaces). */
const project = (velocity: number, rate = 0.99) => ((velocity / 1000) * rate) / (1 - rate);

const DismissContext = createContext<() => void>(() => undefined);

/** A button that closes the sheet it sits in, along the same path it arrived. */
export function Dismiss({ className = 'secondary', children }: { className?: string; children: ReactNode }) {
  const dismiss = useContext(DismissContext);
  return <button type="button" className={className} onClick={dismiss}>{children}</button>;
}

export function Modal({ title, kicker, onClose, children, footer }: {
  title: string; kicker?: string; onClose: () => void; children: ReactNode; footer?: ReactNode;
}) {
  const card = useRef<HTMLDivElement>(null);
  const [leaving, setLeaving] = useState(false);
  const drag = useRef<{ id: number; y0: number; samples: [number, number][] } | null>(null);

  // Leave the way it came: slide and fade out, then unmount. Reduced motion skips straight to close.
  const close = useCallback(() => {
    if (leaving) return;
    if (prefersReducedMotion()) { onClose(); return; }
    setLeaving(true);
    window.setTimeout(onClose, EXIT_MS);
  }, [leaving, onClose]);
  useEscape(close);

  // Focus moves into the sheet, Tab stays inside it, and focus returns to the trigger on close.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    const el = card.current;
    const first = el?.querySelector<HTMLElement>('.modal-body ' + FOCUSABLE) ?? el?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !el) return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!items.length) return;
      const a = items[0]!, z = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); trigger?.focus?.({ preventScroll: true }); };
  }, []);

  // Swipe down to dismiss on the closed Fold: tracks the finger 1:1, resists upward, and
  // decides from the projected resting point, so a short flick is enough.
  const isSheet = () => window.matchMedia('(max-width: 720px)').matches;
  const onPointerDown = (e: React.PointerEvent) => {
    const t = e.target as HTMLElement;
    if (!isSheet() || leaving || t.closest('.modal-body, .modal-foot, button, input, select, textarea, label')) return;
    card.current?.setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, y0: e.clientY, samples: [[e.timeStamp, 0]] };
    card.current?.classList.add('dragging');
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current; const el = card.current;
    if (!d || !el || e.pointerId !== d.id) return;
    const dy = e.clientY - d.y0;
    const y = dy >= 0 ? dy : -(Math.abs(dy) * 0.55 * 60) / (60 + 0.55 * Math.abs(dy)); // rubber-band upward
    el.style.transform = `translateY(${y}px)`;
    d.samples = [...d.samples.slice(-4), [e.timeStamp, dy]];
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current; const el = card.current;
    if (!d || !el || e.pointerId !== d.id) return;
    drag.current = null;
    el.classList.remove('dragging');
    const [t0, y0] = d.samples[0]!; const [t1, y1] = d.samples[d.samples.length - 1]!;
    const v = t1 > t0 ? ((y1 - y0) / (t1 - t0)) * 1000 : 0;
    if (y1 + project(v) > el.offsetHeight * 0.4) {
      // Keep the finger's position; the exit animation continues from it.
      el.style.setProperty('--from', `${Math.max(0, y1)}px`);
      close();
    } else {
      el.style.transform = '';
    }
  };

  return (
    <div className={`modal ${leaving ? 'leaving' : ''}`} role="presentation" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div ref={card} className="modal-card" role="dialog" aria-modal="true" aria-label={title}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <span className="grabber" aria-hidden="true" />
        <button type="button" className="modal-x" aria-label="Close" onClick={close}>×</button>
        <h2 className="modal-title">{title}</h2>
        {kicker && <p className="modal-sub">{kicker}</p>}
        <DismissContext.Provider value={close}>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-foot">{footer}</div>}
        </DismissContext.Provider>
      </div>
    </div>
  );
}

export interface TrendPoint { key: string; label: string; value: number | null; cap: number | null; selected?: boolean }

/** Six-month bars against each month's own ceiling. Unknown months draw no bar, never a zero. */
export function TrendChart({ points, onSelect, title }: { points: TrendPoint[]; onSelect?: (key: string) => void; title: string }) {
  const W = 360, H = 156, top = 18, base = 122, gap = 10;
  const max = Math.max(1, ...points.flatMap((p) => [p.value ?? 0, p.cap ?? 0])) * 1.08;
  const bw = (W - gap * (points.length + 1)) / Math.max(points.length, 1);
  const y = (v: number) => base - ((base - top) * v) / max;
  const short = (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}m` : `${Math.round(v / 1000)}k`);
  return (
    <figure style={{ margin: 0 }}>
      <svg className="trend" viewBox={`0 0 ${W} ${H}`} role={onSelect ? 'group' : 'img'} aria-label={title}>
        {points.map((p, i) => {
          const x = gap + i * (bw + gap);
          const over = p.value !== null && p.cap !== null && p.value > p.cap;
          const label = `${p.label}: ${p.value === null ? 'not published' : short(p.value)}${p.cap !== null ? ` of ${short(p.cap)} ceiling` : ''}`;
          return (
            <g key={p.key} onClick={onSelect ? () => onSelect(p.key) : undefined} style={{ cursor: onSelect ? 'pointer' : 'default' }}
              {...(onSelect ? { tabIndex: 0, role: 'button', 'aria-label': label, 'aria-current': p.selected ? 'true' : undefined,
                onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(p.key); } } } : {})}>
              <title>{label}</title>
              {p.value !== null && (
                <rect x={x} y={y(p.value)} width={bw} height={base - y(p.value)} rx={4}
                  className={`${over ? 'bar-over' : 'bar-actual'} ${p.selected ? 'bar-current' : ''}`} />
              )}
              {p.cap !== null && <line className="ceiling" x1={x - 3} x2={x + bw + 3} y1={y(p.cap)} y2={y(p.cap)} />}
              {p.value !== null && <text x={x + bw / 2} y={y(p.value) - 4} textAnchor="middle">{short(p.value)}</text>}
              <text x={x + bw / 2} y={base + 19} textAnchor="middle" style={p.selected ? { fill: 'var(--paper)', fontWeight: 700 } : undefined}>{p.label}</text>
            </g>
          );
        })}
        <line className="baseline" x1={0} x2={W} y1={base} y2={base} />
      </svg>
      <figcaption className="trend-legend">
        <span><i style={{ background: 'var(--within)' }} />Within ceiling</span>
        <span><i style={{ background: 'var(--over)' }} />Over ceiling</span>
        <span><svg className="legend-line" viewBox="0 0 16 10" aria-hidden="true"><line x1="0" x2="16" y1="5" y2="5" /></svg>Ceiling</span>
      </figcaption>
    </figure>
  );
}
